/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CircularProgress from '@material-ui/core/CircularProgress';
import green from '@material-ui/core/colors/green';
import Grid from '@material-ui/core/Grid/Grid';
import Fab from '@material-ui/core/Fab';
import CheckIcon from '@material-ui/icons/Check';
import SendIcon from '@material-ui/icons/Send';
import ClearIcon from '@material-ui/icons/Clear';
import { bool, func } from 'prop-types';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        margin: 0,
    },
    icon: {
        height: '20px',
    },
    button: {
        backgroundColor: theme.palette.colorPrimary,
        '&:hover': {
            backgroundColor: theme.palette.colorPrimary,
        },
        height: '2.8em',
        width: '3em',
        'border-radius': '3px',
        '&:disabled': {
            color: theme.palette.white,
            backgroundColor: theme.palette.common.gray,
        },
    },
    wrapper: {
        position: 'relative',
    },
    buttonSuccess: {
        backgroundColor: '#86aa35',
        '&:hover': {
            backgroundColor: '#9ec447',
        },
        height: '2.8em',
        width: '3.1em',
        'border-radius': '5px',
        '&:disabled': {
            color: theme.palette.white,
            backgroundColor: theme.palette.common.gray,
        },
    },
    buttonError: {
        backgroundColor: theme.palette.chatbotErrorColor,
        '&:hover': {
            backgroundColor: theme.palette.chatbotErrorHoverColor,
        },
        height: '2.8em',
        width: '3.1em',
        'border-radius': '5px',
        '&:disabled': {
            color: theme.palette.white,
            backgroundColor: theme.palette.common.gray,
        },
    },
    fabProgress: {
        color: theme.palette.white,
        position: 'absolute',
        top: 10,
        left: 14,
        zIndex: 1,
    },
    buttonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
}));

const useMountEffect = (fun, args) => useEffect(fun, args);

export default function CircularIntegration(props) {
    const { error, isDisabled, onClick } = props;
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [buttonClassName, setButtonClassName] = useState(classes.button);
    const [loadingTimeMs] = useState(800);
    const timer = useRef();

    useMountEffect(() => {
        if (props.error) {
            setSuccess(false);
            setButtonClassName(classes.buttonError);
        } else if (props.isTyping) {
            setSuccess(true);
            setButtonClassName(classes.button);
        } else {
            setLoading(true);
            setSuccess(true);
            timer.current = setTimeout(() => {
                setSuccess(!props.error);
                setButtonClassName(classes.button);
                setLoading(false);
            }, loadingTimeMs);
        }
    }, [error, isDisabled]);

    useMountEffect(() => {
        setLoading(false);
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    let icon;
    if (loading) {
        icon =  <CircularProgress size={20} className={classes.fabProgress} />;
    } else if (success && !isDisabled) {
        icon = <SendIcon className={classes.icon} />;
    } else if (success) {
        icon = <CheckIcon className={classes.icon} />;
    } else {
        icon = <ClearIcon className={classes.icon} />;
    }

    return (
        <Grid container alignItems="flex-start" className={classes.root}>
            <Grid item xs className={classes.wrapper}>
                <Fab
                    aria-label="save"
                    color="primary"
                    disabled={isDisabled}
                    className={buttonClassName}
                    onClick={onClick}
                >
                    {icon}
                </Fab>
            </Grid>
        </Grid>
    );
}

CircularIntegration.propTypes = {
    error: bool.isRequired,
    isDisabled: bool.isRequired,
    isTyping: bool.isRequired,
    onClick: func.isRequired,
};
